import React from "react";
import { Job } from "../types";
import { MapPinIcon, CalendarIcon, ExternalLinkIcon } from "../utils/icons";
import logosData from "../utils/logos.json";
import LiquidGlassContainer from "./LiquidGlassContainer";
import "../styles/JobBoard.css";

interface JobCardProps {
  job: Job;
  onApply: (job: Job) => void;
}
/*
const onApply = (job : Job) => {
  window.location.href = job.link;
}
*/
const JobCard: React.FC<JobCardProps> = ({ job, onApply }) => {
  const getCompanyLogo = (companyName: string) => {
    const logoEntry = logosData.find(
      (logo) => logo.company.toLowerCase() === companyName.toLowerCase()
    );
    return logoEntry ? logoEntry.logo : null;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    // split year, month, day which are separated by hyphens
    const dateParts = dateString.split("-");
    const date = new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]));
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getJobTypeColor = (type: string) => {
    switch (type) {
      case "full-time":
        return "bg-haig-surface text-haig-primary border border-haig-border";
      case "part-time":
        return "bg-blue-50 text-haig-secondary border border-haig-border";
      case "contract":
        return "bg-haig-surface text-haig-muted border border-haig-border";
      case "internship":
        return "bg-amber-50 text-haig-primary border border-haig-border";
      default:
        return "bg-haig-surface text-haig-muted border border-haig-border";
    }
  };

  return (
    <LiquidGlassContainer borderRadius={8} tintOpacity={0.22}>
      <div className="transition-shadow duration-300">
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-start space-x-4 flex-1">
          {/* Company Logo */}
          {getCompanyLogo(job.company) ? (
            <div className="flex-shrink-0">
              <img
                src={require(`../../public/assets/logos/${getCompanyLogo(
                  job.company
                )}`)}
                alt={`${job.company} logo`}
                className="w-20 h-20 object-contain rounded-haig mt-1"
              />
            </div>
          ) : (
            <div className="flex-shrink-0 w-12 h-12 bg-haig-surface rounded-haig border border-haig-border flex items-center justify-center">
              <span className="text-xs text-haig-muted font-semibold">
                {job.company.charAt(0).toUpperCase()}
              </span>
            </div>
          )}

          {/* Job Details */}
          <div className="flex-1">
            <h3 className="job-glass-title text-xl font-bold text-haig-primary tracking-tight">
              <a
                href={job.link}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-haig-secondary transition-colors duration-200 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {job.title}
              </a>
            </h3>
            <p className="text-lg text-haig-text mb-1 font-medium">{job.company}</p>
            {job.location && job.location.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 text-haig-muted mb-3">
                <div className="flex items-center">
                  <MapPinIcon className="h-4 w-4 mr-1 shrink-0" />
                  <span className="text-sm">
                    {job.location.every((loc) => loc === "")
                      ? "See Job Posting"
                      : job.location.length > 1
                      ? "Multiple Locations"
                      : job.location[0]}
                  </span>
                </div>
                {job.remote && (
                  <span className="px-2 py-0.5 bg-haig-accent text-haig-primary text-xs font-semibold rounded-full">
                    Remote
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end">
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold ${getJobTypeColor(
              job.type
            )}`}
          >
            {job.type.replace("-", " ")}
          </span>
          {job.salary && (
            <p className="text-sm text-haig-muted mt-1">{job.salary}</p>
          )}
        </div>
      </div>

      <p className="job-glass-muted text-haig-text mb-4 leading-relaxed">{job.description}</p>

      <div className="flex flex-wrap gap-2 mb-4">
        {job.tags.slice(0, 4).map((tag, index) => (
          <span
            key={index}
            className="px-2 py-1 bg-haig-surface text-haig-muted text-xs font-medium rounded-haig border border-haig-border"
          >
            {tag}
          </span>
        ))}
        {job.tags.length > 4 && (
          <span className="px-2 py-1 bg-haig-surface text-haig-muted text-xs rounded-haig border border-haig-border">
            +{job.tags.length - 4}
          </span>
        )}
      </div>

      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex items-center text-haig-muted text-sm">
          <CalendarIcon className="h-4 w-4 mr-1 shrink-0" />
          <span>Retrieved {formatDate(job.date)}</span>
          <span className="mx-2">•</span>
          <span>via {job.company} Careers</span>
        </div>
        <button
          type="button"
          onClick={() => onApply(job)}
          className="flex items-center px-5 py-2.5 bg-haig-secondary text-white rounded-haig font-semibold hover:brightness-95 transition-all duration-200"
        >
          <ExternalLinkIcon className="h-4 w-4 mr-2" />
          Apply Now
        </button>
      </div>
      </div>
    </LiquidGlassContainer>
  );
};

export default JobCard;
